import React, { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

import { UserContext } from '../context/UserContext'
import Panel from '../ui/Panel'
import { isTrial } from '../access'
import Btn from '../ui/Btn'
import Avatar from '../ui/Avatar'
import './user.css'

/*
 * IDENTITY — the first half of the scouting report.
 *
 * Name, position and the physical profile, then the two decisions a scout can
 * act on from here: open a chat room with the player, or keep them on the
 * watchlist. Both were already wired to the API and are untouched:
 *
 *   GET  manager/dashboard/userprofile/{uuid}   the player record
 *   POST manager/dashboard/checkfav             { CustomerUUID, UserUUID }
 *   POST manager/dashboard/setfav               { CustomerUUID, UserUUID, IsFav }
 *   POST manager/rooms                          { HostID, GuestID }
 *
 * The online check is the same socket conversation as before — 'check-if-online'
 * out, 'checked-if-online' back — but the listener is now registered once and
 * removed on unmount instead of being re-bound on every render, and the polled
 * id lives in a ref so the 10s interval never reads a stale closure.
 */

/* sessionStorage throws in locked-down browsers; a profile must not die for it. */
const ss = (key) => {
  try {
    return sessionStorage.getItem(key) || ''
  } catch (e) {
    return ''
  }
}

const auth = () => ({
  headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken") }
})

const orientationOf = (value) => {
  const n = Number(value)
  if (n === 1) return 'Right handed / footed'
  if (n === 2) return 'Left handed / footed'
  return 'Both left & right'
}

const countOf = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const textOf = (value) => {
  const out = value === null || value === undefined ? '' : String(value).trim()
  return out.length > 0 ? out : null
}

export default function UserDetails(params) {
  const [usr, setUsr] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [notice, setNotice] = useState(null)
  const [isOnline, setIsOnline] = useState(0)
  const [isFav, setIsFav] = useState(0)
  const [busyFav, setBusyFav] = useState(false)
  const [busyChat, setBusyChat] = useState(false)

  const { socket } = useContext(UserContext)
  const navigate = useNavigate()

  const idRef = useRef(null)
  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    loadData()

    const intervalId = setInterval(() => {
      checkIfOnline(idRef.current)
    }, 10000) // same 10s heartbeat as before

    return () => {
      aliveRef.current = false
      clearInterval(intervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.uuid])

  useEffect(() => {
    if (!socket) return undefined
    const onChecked = (value) => {
      if (aliveRef.current) setIsOnline(value)
    }
    socket.on('checked-if-online', onChecked)
    return () => {
      socket.off('checked-if-online', onChecked)
    }
  }, [socket])

  const checkIfOnline = (id) => {
    if (id && socket) {
      socket.emit('check-if-online', id)
    }
  }

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)

    axios.get(process.env.REACT_APP_API_SERVER + 'manager/dashboard/userprofile/' + params.uuid, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
      }
    })
      .then(res => {
        const yearNow = new Date().getFullYear()
        const record = (res.data && res.data.results) ? res.data.results : {}

        // Age is derived from the birth year exactly as before — guarded so a
        // record without a date of birth renders instead of throwing.
        const bornYear = record.DateOfBirthYMD
          ? Number(String(record.DateOfBirthYMD).split('-')[0])
          : NaN
        record.Age = Number.isFinite(bornYear) ? yearNow - bornYear : null

        setUsr(record)
        idRef.current = record.id
        checkIfOnline(record.id)

        const obj = {
          CustomerUUID: sessionStorage.getItem('userToken'),
          UserUUID: params.uuid
        }

        axios.post(process.env.REACT_APP_API_SERVER + 'manager/dashboard/checkfav', obj, {
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
          }
        })
          .then(res11 => {
            if (!aliveRef.current) return
            setIsFav(res11.data.IsFav)
            setIsLoading(false)
          })
          .catch(() => {
            // The profile is already usable; only the star state is unknown.
            if (aliveRef.current) setIsLoading(false)
          })
      })
      .catch(err => {
        if (!aliveRef.current) return
        setIsLoading(false)
        setFailed(true)
      })
  }

  const createRoom = () => {
    const obj = {
      HostID: sessionStorage.getItem('ChatUserUUID'),
      GuestID: params.uuid
    }

    setBusyChat(true)
    setNotice(null)

    axios.post(process.env.REACT_APP_API_SERVER + 'manager/rooms', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
      }
    }).then(async res => {
      if (res.data.err == 0) {
        if (socket) await socket.emit('join-room', res.data.RoomIDUUID)
        navigate('/chatroom/' + res.data.RoomIDUUID)
      } else {
        if (!aliveRef.current) return
        setBusyChat(false)
        setNotice({
          tone: 'crit',
          title: 'The chat room could not be opened.',
          text: 'The server refused the request. Nothing was sent to the player — try again in a moment.'
        })
      }
    }).catch(() => {
      if (!aliveRef.current) return
      setBusyChat(false)
      setNotice({
        tone: 'crit',
        title: 'The chat room could not be opened.',
        text: 'The server did not answer. Nothing was sent to the player — try again in a moment.'
      })
    })
  }

  const fav = () => {
    const next = isFav == 0 ? 1 : 0
    const obj = {
      CustomerUUID: sessionStorage.getItem('userToken'),
      UserUUID: params.uuid,
      IsFav: next
    }

    setBusyFav(true)
    setNotice(null)

    axios.post(process.env.REACT_APP_API_SERVER + 'manager/dashboard/setfav', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
      }
    })
      .then(res => {
        if (!aliveRef.current) return
        setBusyFav(false)
        if (res.data.err == 0) {
          loadData()
          setNotice({
            tone: 'good',
            title: next === 1 ? 'Added to your watchlist.' : 'Removed from your watchlist.',
            text: next === 1
              ? 'Everything this player posts now shows up in your watchlist feed.'
              : 'They will no longer appear in your watchlist or its activity feed.'
          })
        } else {
          setNotice({
            tone: 'crit',
            title: 'Your watchlist was not changed.',
            text: 'The server refused the change. The star still shows the last saved state.'
          })
        }
      })
      .catch(() => {
        if (!aliveRef.current) return
        setBusyFav(false)
        setNotice({
          tone: 'crit',
          title: 'Your watchlist was not changed.',
          text: 'The server did not answer. The star still shows the last saved state.'
        })
      })
  }

  const fullName = [textOf(usr.FirstName), textOf(usr.LastName)].filter(Boolean).join(' ')

  // The video modal in the sibling panels needs to name the player, and this is
  // the only component that already fetches the profile.
  useEffect(() => {
    if (fullName && typeof params.onName === 'function') params.onName(fullName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullName])
  const age = Number.isFinite(usr.Age) ? usr.Age : null
  const isMinor = age !== null && age < 16
  const canChat = ss('ChatUserUUID').length > 0 && age !== null && age >= 16
  const position = textOf(usr.Position)
  const country = textOf(usr.Country)
  const nickname = textOf(usr.Nickname)
  const bio = textOf(usr.PersonalInfo)
  const height = textOf(usr.Height)
  const weight = textOf(usr.Weight)

  const facts = [
    ['Age', age !== null ? age : '—'],
    ['Height', height ? height + ' cm' : '—'],
    ['Weight', weight ? weight + ' kg' : '—'],
    ['Country', country || '—'],
    ['Orientation', orientationOf(usr.LeftRight)]
  ]

  return (
    <Panel className="usr-id">

      {isLoading && (
        <div className="usr-skel">
          <div className="skeleton usr-skel-avatar"></div>
          <div className="skeleton usr-skel-line"></div>
          <div className="skeleton usr-skel-line usr-skel-short"></div>
          <div className="skeleton usr-skel-block"></div>
          <div className="skeleton usr-skel-block"></div>
        </div>
      )}

      {!isLoading && failed && (
        <div className="usr-alert" role="alert">
          <b>This player could not be loaded.</b>
          <span>The server did not answer. Nothing has been lost — try again.</span>
          <Btn onClick={loadData}>Try again</Btn>
        </div>
      )}

      {!isLoading && !failed && (
        <>
          <div className="usr-id-head">
            {/* 108px is exactly the box the old .usr-avatar div occupied */}
            <Avatar uuid={params.uuid} name={fullName} size={108} />

            <div className="usr-id-who">
              <h2 className="usr-name">{fullName || 'Unnamed player'}</h2>
              <div className="usr-sub">{country || 'Country not set'}</div>

              <div className="usr-chips">
                <span className="usr-chip usr-chip-position">
                  {position || 'Position not set'}
                </span>
                {nickname && <span className="usr-chip">Tag {nickname}</span>}
                {isOnline == 1 && (
                  <span className="usr-chip usr-chip-live">
                    <span className="usr-pulse" aria-hidden="true"></span>
                    Active now
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="usr-actions">
            {/* Not offered on a free trial — see noTrial.js on the server. */}
            {canChat && !isTrial() && (
              <Btn variant="accent" onClick={createRoom} disabled={busyChat}>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l.9-4.4A8 8 0 1 1 20 12z" />
                </svg>
                {busyChat ? 'Opening…' : 'Message player'}
              </Btn>
            )}

            <Btn
              onClick={fav}
              disabled={busyFav}
              aria-pressed={isFav == 1}
              title={isFav == 1 ? 'Remove from watchlist' : 'Add to watchlist'}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" className={isFav == 1 ? 'usr-heart-on' : undefined}>
                <path d="M12 20.5S3.5 15.2 3.5 9.4A4.4 4.4 0 0 1 12 7.4a4.4 4.4 0 0 1 8.5 2c0 5.8-8.5 11.1-8.5 11.1z" />
              </svg>
              {isFav == 1 ? 'On watchlist' : 'Add to watchlist'}
            </Btn>
          </div>

          {notice && (
            <div
              className={notice.tone === 'good' ? 'usr-alert usr-alert-good' : 'usr-alert'}
              role="status"
            >
              <b>{notice.title}</b>
              <span>{notice.text}</span>
            </div>
          )}

          <div className="usr-counts">
            <div className="usr-count">
              <span className="usr-count-value">{countOf(usr.xNumOfPosts)}</span>
              <span className="mono usr-count-label">Posts</span>
            </div>
            <div className="usr-count">
              <span className="usr-count-value">{countOf(usr.xNumOfChallenges)}</span>
              <span className="mono usr-count-label">Challenges done</span>
            </div>
          </div>

          <div className="mono usr-section">Physical profile</div>

          <div className="usr-facts">
            {facts.map(([label, value]) => (
              <div className="usr-fact" key={label}>
                <span className="mono usr-fact-label">{label}</span>
                <span className="usr-fact-value">{value}</span>
              </div>
            ))}
          </div>

          {bio && <p className="usr-bio">{bio}</p>}

          {isMinor && (
            <div className="callout">
              <b>Under 16.</b> Direct messaging is closed for this player. Contact goes through
              the parent or guardian: {textOf(usr.ParentEmail) || 'no parent email on file'}.
            </div>
          )}
        </>
      )}

    </Panel>
  )
}
